import { createApi, fetchBaseQuery, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { msalInstance } from '../auth/AuthProvider';
import { loginRequest } from '../auth/msalConfig';
import { config } from '../utils/config';

// Define the base query
const baseQuery = fetchBaseQuery({
  baseUrl: config.api.url || 'http://localhost:3000/api',
  responseHandler: async (response) => {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    // Handle plain text or other non-JSON responses
    const text = await response.text();
    return { data: text };
  },
  prepareHeaders: async (headers) => {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const response = await msalInstance.acquireTokenSilent({
          scopes: loginRequest.scopes.filter((scope): scope is string => scope !== undefined),
          account: accounts[0]
        });
        headers.set('Authorization', `Bearer ${response.accessToken}`);
      }
    } catch (error) {
      console.error('Error acquiring token:', error);
    }

    return headers;
  },
});

// Create an interceptor for the base query
const baseQueryWithInterceptor: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  
  // Handle parsing errors for 200 responses
  if (result.error?.status === 'PARSING_ERROR' && result.error.originalStatus === 200) {
    // Convert parsing error with 200 status into successful response
    return {
      data: result.error.data
    };
  }
  
  if (result.error) {
    if (result.error.status === 401) {
      // Handle unauthorized requests
      console.error('Unauthorized request');
    }
    console.error('API Error:', result.error);
  }
  return result;
};

// Define response type
export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

// Create the base API
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithInterceptor,
  tagTypes: ['Transactions'],
  endpoints: () => ({}),
});
